<script setup lang="ts">
	import { computed, ref, watch } from 'vue';
	import leftArrow from '@/assets/presentation/left-arrow.svg';
	import { getOrGenerateQrForUrl, isValidPoiUrl } from '@/composables/usePoiUrlQrCache';
	import { formatEventDateFacebookStyle } from '@/composables/formatEventDate';
	import { useMapStoreData } from '@/stores/index';

	type EventItem = {
		title?: string;
		start?: string;
		end?: string;
		content?: string;
		image_xl?: string;
		url?: string;
	};

	const props = defineProps<{
		activeEvent: EventItem | null;
	}>();

	const emit = defineEmits<{
		(e: 'close'): void;
	}>();

	const qrDataUrl = ref('');
	const mapStore = useMapStoreData();

	const isVisible = computed(() => props.activeEvent !== null);
	const formattedStart = computed(() =>
		formatEventDateFacebookStyle(props.activeEvent?.start, mapStore.currentLanguage || 'en')
	);
	const formattedEnd = computed(() => formatEventDateFacebookStyle(props.activeEvent?.end, mapStore.currentLanguage || 'en'));

	watch(
		() => props.activeEvent?.url,
		async (url) => {
			qrDataUrl.value = '';
			if (typeof url !== 'string' || !isValidPoiUrl(url)) return;
			const generated = await getOrGenerateQrForUrl(url);
			qrDataUrl.value = generated || '';
		},
		{ immediate: true }
	);

	function closePresentation() {
		emit('close');
	}
</script>

<template>
	<Transition name="fade">
		<div v-if="isVisible" class="event-presentation">
			<div class="content-panel">
				<h3 class="title">
					<img @click="closePresentation()" :src="leftArrow" alt="" />
					{{ activeEvent?.title || '' }}
				</h3>
				<div class="date" v-if="activeEvent?.start || activeEvent?.end">
					{{ formattedStart }}
					<span v-if="formattedStart && formattedEnd"> - </span>
					{{ formattedEnd }}
				</div>
				<div class="text-content">
					<div class="description">{{ activeEvent?.content || '' }}</div>
				</div>
			</div>

			<div class="media-panel">
				<div class="hero-image" :style="activeEvent?.image_xl ? { backgroundImage: 'url(' + activeEvent?.image_xl + ')' } : {}"></div>
			</div>

			<div v-if="qrDataUrl" class="qr-icon-container qr-icon-container--left" :title="activeEvent?.url">
				<img class="qr-icon-img" :src="qrDataUrl" alt="QR Code" />
			</div>
		</div>
	</Transition>
</template>

<style scoped lang="scss">
	@import '../assets/style/mixins.scss';

	.event-presentation {
		position: fixed;
		top: get-vw(120px);
		left: 0;
		width: 100vw;
		height: calc(100vh - get-vw(120px));
		z-index: 90;
		display: flex;
		background: #fff;
	}

	.content-panel {
		width: 50%;
		padding: get-vw(40px);
		overflow: hidden;
		display: flex;
		flex-direction: column;
	}

	.title {
		font-size: get-vw(42px);
		font-weight: 700;
		line-height: 1.15;
		color: #2e3d45;
		display: flex;
		gap: get-vw(16px);
		align-items: center;
		margin-bottom: get-vw(14px);

		img {
			width: get-vw(42px);
			cursor: pointer;
		}
	}

	.date {
		font-size: get-vw(20px);
		font-weight: 700;
		color: #145a70;
		margin-bottom: get-vw(16px);
	}

	.text-content {
		overflow-y: auto;
		padding-right: get-vw(8px);
	}

	.description {
		color: #2e3d45;
		font-size: get-vw(20px);
		line-height: 1.4;
		white-space: pre-line;
	}

	.media-panel {
		width: 50%;
		position: relative;
	}

	.hero-image {
		width: 100%;
		height: 100%;
		background-size: cover;
		background-position: center;
		background-repeat: no-repeat;
		background-color: #f1f1f1;
	}

	.qr-icon-container {
		position: absolute;
		right: get-vw(30px);
		bottom: get-vw(30px);
		width: get-vw(120px);
		height: get-vw(120px);
		background: #fff;
		border-radius: get-vw(12px);
		padding: get-vw(8px);
		box-shadow: 0 get-vw(2px) get-vw(10px) rgba(0, 0, 0, 0.2);
	}

	.qr-icon-img {
		display: block;
		width: 100%;
		height: 100%;
	}
</style>
